import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/Logo";

function formatDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors focus:border-[#D4AF37] focus:bg-white/[0.07]";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[380px] w-[380px] -translate-x-1/2 rounded-full bg-[#D4AF37]/8 blur-[140px]" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/10 bg-[#0D0D0F] p-8 sm:p-10" data-testid="admin-login-card">
          <div className="flex flex-col items-center text-center">
            <LogoMark className="h-12 w-auto" />
            <h1 className="mt-4 font-display text-3xl uppercase tracking-wide text-white">
              Espace <span className="text-gold-gradient">Pro</span>
            </h1>
            <p className="mt-2 text-sm text-zinc-500">Gestion des rendez-vous Motor Select</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <input
              type="email"
              required
              data-testid="admin-email-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
            />
            <input
              type="password"
              required
              data-testid="admin-password-input"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </div>
        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 transition-colors hover:text-[#E6CA65]"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Retour au site
        </Link>
      </motion.div>
    </div>
  );
}
